import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from 'api/api';
import TokenUtils from 'utils/TokenUtils';
import { addNotification } from './notificationsSlice';

export const getCoaches = createAsyncThunk('coaches/getCoaches', async (searchQuery, { rejectWithValue }) => {
  try {
    const response = await api.get('/coaches', {
      params: { search: searchQuery },
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати дані тренерів');
  }
});

export const getCoach = createAsyncThunk('coaches/getCoach', async (coachID, { rejectWithValue }) => {
  try {
    const response = await api.get(`/coaches/${coachID}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати дані тренера');
  }
});

export const createCoach = createAsyncThunk('coaches/createCoach', async (coachData, { dispatch, rejectWithValue }) => {
  try {
    const token = TokenUtils.getToken();
    const response = await api.post('/coaches', coachData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data) {
      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Тренера додано успішно',
          type: 'success',
        })
      );
    }

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося додати тренера');
  }
});

export const updateCoach = createAsyncThunk(
  'coaches/updateCoach',
  async (updatedData, { rejectWithValue, dispatch }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.put('/coaches', updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Дані тренера оновлено',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося оновити дані тренера');
    }
  }
);

export const deleteCoaches = createAsyncThunk('coaches/deleteCoaches', async (coachIDs, { rejectWithValue }) => {
  try {
    const token = TokenUtils.getToken();
    const response = await api.delete('/coaches', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: { ids: coachIDs },
    });

    return response.data?.deletedIds;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося видалити тренерів');
  }
});

const coachesSlice = createSlice({
  name: 'coaches',
  initialState: {
    coaches: [],
    error: null,
    isLoading: false,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCoaches.fulfilled, (state, action) => {
        state.coaches = action.payload;
      })
      .addCase(getCoaches.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося отримати дані тренерів';
      })
      .addCase(getCoach.fulfilled, (state, action) => {
        const existingIndex = state.coaches.findIndex((c) => c.id === action.payload.id);

        if (existingIndex !== -1) {
          state.coaches[existingIndex] = action.payload;
        } else {
          state.coaches.unshift(action.payload);
        }
      })
      .addCase(getCoach.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося отримати дані тренера';
      })
      .addCase(createCoach.fulfilled, (state, action) => {
        state.coaches = [action.payload, ...state.coaches];
      })
      .addCase(createCoach.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося додати тренера';
      })
      .addCase(updateCoach.fulfilled, (state, action) => {
        const index = state.coaches.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.coaches[index] = action.payload;
        }
      })
      .addCase(updateCoach.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося оновити дані тренера';
      })
      .addCase(deleteCoaches.fulfilled, (state, action) => {
        state.coaches = state.coaches.filter((coach) => !action.payload.includes(coach.id));
      })
      .addCase(deleteCoaches.rejected, (state, action) => {
        state.error = action.payload || 'Не вдалося видалити тренерів';
      })
      .addMatcher(
        (action) => action.type.endsWith('/pending'),
        (state) => {
          state.isLoading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith('/fulfilled') || action.type.endsWith('/rejected'),
        (state) => {
          state.isLoading = false;
        }
      );
  },
});

export default coachesSlice.reducer;
